import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Download, Trash2, Mail, Save } from "lucide-react";
import { api, BACKEND_URL } from "@/lib/auth";
import { fmtDateTime } from "@/lib/datetime";

const STATUSES: Array<{ key: string; label: string }> = [
    { key: "neu", label: "Neu" },
    { key: "kontaktiert", label: "Kontaktiert" },
    { key: "angeboten", label: "Angebot" },
    { key: "auftrag", label: "Auftrag" },
    { key: "erledigt", label: "Erledigt" },
];

function statusChipStyle(status: string): React.CSSProperties {
    if (status === "neu") return { background: "hsl(var(--hf-gold-soft) / 0.7)", color: "hsl(var(--hf-ink))" };
    if (status === "kontaktiert") return { background: "hsl(210 30% 88%)", color: "hsl(var(--hf-ink))" };
    if (status === "angeboten") return { background: "hsl(35 60% 80%)", color: "hsl(var(--hf-ink))" };
    if (status === "auftrag") return { background: "hsl(140 40% 82%)", color: "hsl(var(--hf-ink))" };
    if (status === "erledigt") return { background: "hsl(var(--hf-surface-2))", color: "hsl(var(--hf-muted))" };
    return { background: "hsl(var(--hf-surface))", color: "hsl(var(--hf-ink))" };
}

type Req = {
    id: string; created_at: string; status: string;
    name: string; email: string; phone: string; firma?: string;
    object_type: string; location: string; grundstueck: string;
    wunschtermin: string; package?: string; message: string;
    note?: string;
};

type Payload = {
    rep: { id: string; number: number | null; code: string; name: string };
    items: Req[];
    count: number;
};

export default function AdminVertreterRequestsPage() {
    const { id = "" } = useParams();
    const nav = useNavigate();
    const [data, setData] = useState<Payload | null>(null);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        const res = await api<Payload>(`/api/admin/representatives/${id}/requests`);
        if (res.ok && res.data) setData(res.data);
        else setError(res.error || "Anfragen konnten nicht geladen werden.");
        setLoading(false);
    }, [id]);

    useEffect(() => { void load(); }, [load]);

    const setStatus = async (rid: string, status: string) => {
        const res = await api<Req>(`/api/admin/contact-requests/${rid}`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ status }),
        });
        if (res.ok && res.data && data) {
            setData({ ...data, items: data.items.map((i) => (i.id === rid ? { ...i, status } : i)) });
        }
    };

    const setNote = async (rid: string, note: string) => {
        const res = await api<Req>(`/api/admin/contact-requests/${rid}`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ note }),
        });
        if (res.ok && res.data && data) {
            setData({ ...data, items: data.items.map((i) => (i.id === rid ? { ...i, note } : i)) });
        }
    };

    const deleteReq = async (rid: string, name: string) => {
        if (!window.confirm(`Anfrage von ${name} wirklich löschen? Kann nicht rückgängig gemacht werden.`)) return;
        const res = await api(`/api/admin/contact-requests/${rid}`, { method: "DELETE" });
        if (res.ok && data) {
            setData({ ...data, items: data.items.filter((i) => i.id !== rid), count: data.count - 1 });
        }
    };

    if (loading) return <div className="text-hf-muted text-sm">Wird geladen…</div>;
    if (error) {
        return (
            <div>
                <button onClick={() => nav(`/admin/vertreter/${id}`)} className="hf-btn hf-btn-ghost">
                    <ArrowLeft size={14} /> Zurück
                </button>
                <div className="mt-6 text-hf-danger">{error}</div>
            </div>
        );
    }
    if (!data) return null;

    const { rep, items, count } = data;
    const numLabel = rep.number != null ? `Nr. ${rep.number}` : "—";

    return (
        <div data-testid="admin-vertreter-requests">
            <button onClick={() => nav(`/admin/vertreter/${id}`)} className="hf-btn hf-btn-ghost">
                <ArrowLeft size={14} /> Zurück zum Vertreter
            </button>

            <div className="mt-4 flex flex-wrap items-baseline justify-between gap-3">
                <div>
                    <div className="hf-eyebrow">Anfragen · Vertreter {numLabel}</div>
                    <h1 className="font-display text-3xl md:text-4xl mt-2">{rep.name}</h1>
                    <p className="mt-1 text-hf-muted">{count} Anfragen insgesamt · sortiert nach Datum absteigend</p>
                </div>
                <a
                    href={`${BACKEND_URL}/api/admin/representatives/${id}/requests.csv`}
                    className="hf-btn hf-btn-primary"
                    data-testid="admin-vertreter-requests-csv"
                >
                    <Download size={14} /> CSV exportieren
                </a>
            </div>

            {items.length === 0 ? (
                <div
                    className="mt-10 p-12 text-center rounded-sm"
                    style={{ background: "hsl(var(--hf-bg))", border: "1px dashed hsl(var(--hf-line))" }}
                >
                    <div className="text-hf-muted">Noch keine Anfragen über {rep.name}.</div>
                </div>
            ) : (
                <div className="mt-8 space-y-6">
                    {items.map((it) => (
                        <RequestCard
                            key={it.id}
                            item={it}
                            onStatus={(s) => void setStatus(it.id, s)}
                            onNote={(n) => void setNote(it.id, n)}
                            onDelete={() => void deleteReq(it.id, it.name)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

function RequestCard({
    item, onStatus, onNote, onDelete,
}: {
    item: Req;
    onStatus: (s: string) => void;
    onNote: (n: string) => void;
    onDelete: () => void;
}) {
    const [note, setNoteLocal] = useState(item.note || "");
    const [savedTick, setSavedTick] = useState(false);
    const [saving, setSaving] = useState(false);

    // Autosave-on-blur — nicht bei jedem Tastendruck (spart Requests).
    const saveNote = async () => {
        if (note === (item.note || "")) return;
        setSaving(true);
        onNote(note);
        setSaving(false);
        setSavedTick(true);
        window.setTimeout(() => setSavedTick(false), 1500);
    };

    return (
        <article
            className="p-6 rounded-sm"
            style={{ background: "hsl(var(--hf-bg))", border: "1px solid hsl(var(--hf-line))" }}
            data-testid={`admin-vertreter-req-card-${item.id}`}
        >
            <header className="flex flex-wrap items-start justify-between gap-3 pb-4"
                    style={{ borderBottom: "1px solid hsl(var(--hf-line))" }}>
                <div>
                    <div className="text-xs text-hf-muted">
                        {fmtDateTime(item.created_at, { dateStyle: "medium", timeStyle: "short" })}
                    </div>
                    <div className="mt-1 font-display text-xl text-hf-ink">{item.name}</div>
                    <div className="mt-1 text-sm text-hf-muted flex flex-wrap items-center gap-x-3 gap-y-1">
                        {item.email && (
                            <a href={`mailto:${item.email}`} className="inline-flex items-center gap-1 hover:text-hf-ink" data-testid={`admin-vertreter-req-mail-${item.id}`}>
                                <Mail size={12} /> {item.email}
                            </a>
                        )}
                        {item.phone && <span>· {item.phone}</span>}
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <select
                        value={item.status}
                        onChange={(e) => onStatus(e.target.value)}
                        className="px-3 py-2 rounded-sm text-xs"
                        style={statusChipStyle(item.status)}
                        data-testid={`admin-vertreter-req-status-${item.id}`}
                    >
                        {STATUSES.map((s) => (
                            <option key={s.key} value={s.key}>{s.label}</option>
                        ))}
                    </select>
                    <button
                        type="button"
                        onClick={onDelete}
                        className="hf-btn hf-btn-ghost"
                        title="Anfrage löschen"
                        data-testid={`admin-vertreter-req-delete-${item.id}`}
                    >
                        <Trash2 size={13} />
                    </button>
                </div>
            </header>

            <dl className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                <Field label="Objektart" value={item.object_type} />
                <Field label="Ort" value={item.location} />
                <Field label="Grundstücksgröße" value={item.grundstueck} />
                <Field label="Wunschtermin" value={item.wunschtermin} />
                {item.package && <Field label="Paket" value={item.package} />}
                {item.firma && <Field label="Firma" value={item.firma} />}
            </dl>

            {item.message && (
                <div className="mt-4">
                    <div className="hf-eyebrow" style={{ fontSize: 10 }}>Nachricht</div>
                    <div
                        className="mt-1 p-3 rounded-sm text-sm text-hf-ink whitespace-pre-wrap"
                        style={{ background: "hsl(var(--hf-surface))", border: "1px solid hsl(var(--hf-line))" }}
                    >
                        {item.message}
                    </div>
                </div>
            )}

            <div className="mt-4">
                <div className="flex items-center justify-between">
                    <div className="hf-eyebrow" style={{ fontSize: 10 }}>Interne Notiz</div>
                    <div className="text-xs text-hf-muted h-4">
                        {saving ? "…speichere" : savedTick ? "Gespeichert." : ""}
                    </div>
                </div>
                <textarea
                    value={note}
                    onChange={(e) => setNoteLocal(e.target.value)}
                    onBlur={() => void saveNote()}
                    rows={3}
                    placeholder="Notiz (wird beim Verlassen des Feldes gespeichert)…"
                    className="mt-1 w-full px-3 py-2 rounded-sm bg-white outline-none text-sm text-hf-ink"
                    style={{ border: "1px solid hsl(var(--hf-line))" }}
                    data-testid={`admin-vertreter-req-note-${item.id}`}
                />
                <div className="mt-1 text-right">
                    <button
                        type="button"
                        onClick={() => void saveNote()}
                        className="hf-btn hf-btn-ghost text-xs"
                        data-testid={`admin-vertreter-req-note-save-${item.id}`}
                    >
                        <Save size={12} /> Notiz speichern
                    </button>
                </div>
            </div>

            <div className="mt-4 pt-3 flex justify-end" style={{ borderTop: "1px solid hsl(var(--hf-line))" }}>
                <Link
                    to={`/admin/anfragen/${item.id}`}
                    className="text-xs text-hf-muted hover:text-hf-ink underline"
                    data-testid={`admin-vertreter-req-open-${item.id}`}
                >
                    Vollständige Anfrage öffnen →
                </Link>
            </div>
        </article>
    );
}

function Field({ label, value }: { label: string; value?: string }) {
    return (
        <div>
            <dt className="text-[10px] tracking-[0.14em] uppercase text-hf-muted">{label}</dt>
            <dd className="text-sm text-hf-ink">{value || "—"}</dd>
        </div>
    );
}
